import { useSidebar } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import {
  Bell,
  Search,
  Moon,
  Sun,
  Monitor,
  Palette,
  Check,
  CreditCard,
  CalendarCheck,
  Megaphone,
  ClipboardList,
  ShieldAlert,
  UserPlus,
  Settings,
  CheckCheck,
  Loader2,
  FlaskConical,
  PanelLeftClose,
  PanelLeftOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { useUIStore, type AccentColor } from "@/stores/ui.store";
import { useDemoMode } from "@/lib/demo-mode";
import { LanguageSwitcher } from "./language-switcher";
import { Breadcrumbs } from "./breadcrumbs";
import { IdleStatus } from "./idle-status";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  useNotificationCounts,
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from "@/features/notifications/api/notifications.queries";
import type { NotificationType } from "@/features/notifications/api/notifications.api";

interface AppHeaderProps {
  onSearchOpen?: () => void;
}

const ACCENT_COLORS: { value: AccentColor; label: string; swatch: string }[] = [
  { value: "blue", label: "Blue", swatch: "bg-blue-500" },
  { value: "green", label: "Green", swatch: "bg-emerald-500" },
  { value: "violet", label: "Violet", swatch: "bg-violet-500" },
  { value: "orange", label: "Orange", swatch: "bg-orange-500" },
  { value: "rose", label: "Rose", swatch: "bg-rose-500" },
  { value: "amber", label: "Amber", swatch: "bg-amber-500" },
  { value: "teal", label: "Teal", swatch: "bg-teal-500" },
  { value: "slate", label: "Slate", swatch: "bg-slate-500" },
];

// ── Notification type config ──────────────────────────────────────────────

const NOTIF_TYPE_CONFIG: Record<
  NotificationType,
  { icon: React.ElementType; color: string; bg: string; label: string }
> = {
  fee_overdue: { icon: CreditCard, color: "text-red-600 dark:text-red-400", bg: "bg-red-100 dark:bg-red-900/40", label: "Fee Overdue" },
  fee_payment: { icon: CreditCard, color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-100 dark:bg-emerald-900/40", label: "Payment" },
  attendance_alert: { icon: CalendarCheck, color: "text-amber-600 dark:text-amber-400", bg: "bg-amber-100 dark:bg-amber-900/40", label: "Attendance" },
  announcement: { icon: Megaphone, color: "text-purple-600 dark:text-purple-400", bg: "bg-purple-100 dark:bg-purple-900/40", label: "Announcement" },
  exam_reminder: { icon: ClipboardList, color: "text-blue-600 dark:text-blue-400", bg: "bg-blue-100 dark:bg-blue-900/40", label: "Exam" },
  incident: { icon: ShieldAlert, color: "text-red-600 dark:text-red-400", bg: "bg-red-100 dark:bg-red-900/40", label: "Incident" },
  leave_request: { icon: UserPlus, color: "text-indigo-600 dark:text-indigo-400", bg: "bg-indigo-100 dark:bg-indigo-900/40", label: "Leave" },
  admission: { icon: UserPlus, color: "text-teal-600 dark:text-teal-400", bg: "bg-teal-100 dark:bg-teal-900/40", label: "Admission" },
  system: { icon: Settings, color: "text-slate-600 dark:text-slate-400", bg: "bg-slate-100 dark:bg-slate-900/40", label: "System" },
};

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── NotificationDropdown ──────────────────────────────────────────────────

function NotificationDropdown() {
  const navigate = useNavigate();
  const { data: counts } = useNotificationCounts();
  const { data: listData, isLoading } = useNotifications({ limit: 15 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const unread = counts?.unread ?? 0;
  const notifications = listData?.data ?? [];

  function handleClick(notif: { id: string; read: boolean; action_url?: string }) {
    if (!notif.read) markRead.mutate(notif.id);
    if (notif.action_url) navigate(notif.action_url);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-8 w-8">
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-0.5 text-[10px] font-bold text-white">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="w-[360px] p-0 rounded-xl shadow-xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">Notifications</span>
            {unread > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-bold">
                {unread}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unread > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
              >
                {markAllRead.isPending
                  ? <Loader2 className="h-3 w-3 animate-spin" />
                  : <CheckCheck className="h-3 w-3" />
                }
                Mark all read
              </Button>
            )}
          </div>
        </div>

        {/* List */}
        <ScrollArea className="h-[400px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <div className="rounded-full bg-muted p-3 mb-3">
                <Bell className="h-5 w-5 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground">All caught up</p>
              <p className="mt-1 text-xs text-muted-foreground">No new notifications</p>
            </div>
          ) : (
            <ul className="divide-y divide-border/50">
              {notifications.map((n) => {
                const cfg = NOTIF_TYPE_CONFIG[n.type] ?? NOTIF_TYPE_CONFIG.system;
                const Icon = cfg.icon;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => handleClick(n)}
                      className={cn(
                        "group w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50",
                        !n.read && "bg-primary/5"
                      )}
                    >
                      {/* Icon */}
                      <div className={cn("mt-0.5 shrink-0 rounded-lg p-1.5", cfg.bg)}>
                        <Icon className={cn("h-3.5 w-3.5", cfg.color)} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className={cn(
                          "text-sm leading-snug truncate",
                          n.read ? "font-normal text-foreground" : "font-semibold text-foreground"
                        )}>
                          {n.title}
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                          {n.body}
                        </p>
                        <p className="mt-1 text-[10px] text-muted-foreground/70">
                          {n.actor_name ? `${n.actor_name} · ` : ""}{timeAgo(n.created_at)}
                        </p>
                      </div>

                      {/* Unread dot */}
                      {!n.read && (
                        <>
                          <span className="mt-1.5 shrink-0 h-2 w-2 rounded-full bg-primary" aria-hidden="true" />
                          <span className="sr-only">(unread)</span>
                        </>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>

        {/* Footer */}
        <div className="border-t px-4 py-2.5">
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-8 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => navigate("/communications/notifications")}
          >
            View all notifications
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── DemoModeBanner ────────────────────────────────────────────────────────

function DemoModeBanner() {
  const { demoMode, toggleDemoMode } = useDemoMode();
  if (!demoMode) return null;
  return (
    <div className="flex items-center justify-center gap-3 bg-amber-500/15 border-b border-amber-500/30 px-4 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
      <FlaskConical className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>
        Demo mode — using seed data.{" "}
        <span className="opacity-70">No real API calls are made.</span>
      </span>
      <button
        type="button"
        onClick={toggleDemoMode}
        className="ml-auto rounded px-2 py-0.5 text-[11px] font-semibold underline underline-offset-2 hover:no-underline transition-colors"
        aria-label="Exit demo mode"
      >
        Exit Demo
      </button>
    </div>
  );
}

// ── AppHeader ─────────────────────────────────────────────────────────────

export function AppHeader({ onSearchOpen }: AppHeaderProps) {
  const setTheme = useUIStore((s) => s.setTheme);
  const theme = useUIStore((s) => s.theme);
  const accentColor = useUIStore((s) => s.accentColor);
  const setAccentColor = useUIStore((s) => s.setAccentColor);
  const { t } = useTranslation("common");
  const { demoMode, toggleDemoMode } = useDemoMode();
  const { state, toggleSidebar } = useSidebar();

  return (
    <div className="sticky top-0 z-30">
      <DemoModeBanner />
      <header className="flex h-14 shrink-0 items-center gap-2 border-b bg-background/80 backdrop-blur-lg px-4 transition-colors">
      <Button
        variant="outline"
        size="icon"
        onClick={toggleSidebar}
        className="-ml-1 h-8 w-8"
        aria-label={state === "expanded" ? "Collapse sidebar" : "Expand sidebar"}
      >
        {state === "expanded"
          ? <PanelLeftClose className="h-4 w-4" />
          : <PanelLeftOpen className="h-4 w-4" />
        }
      </Button>

      {/* Breadcrumbs in header */}
      <div className="hidden md:block">
        <Breadcrumbs />
      </div>

      {/* Search trigger — pushed right on mobile, centered on desktop */}
      <div className="ml-auto flex items-center gap-1.5">
        <button
          onClick={onSearchOpen}
          aria-label={t("search.placeholderShort", "Search")}
          className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground sm:h-9 sm:w-auto sm:max-w-[220px] sm:gap-2 sm:border sm:bg-muted/40 sm:px-3 sm:text-sm"
        >
          <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="hidden sm:inline">{t("search.placeholderShort")}</span>
          <kbd className="ml-auto hidden rounded border bg-background/80 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground lg:inline-flex">
            ⌘K
          </kbd>
        </button>

        <Separator orientation="vertical" className="mx-1 hidden h-4 sm:block" />

        {/* Language switcher */}
        <div className="hidden sm:block">
          <LanguageSwitcher />
        </div>

        {/* Accent color picker */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <Palette className="h-4 w-4" />
              <span className="sr-only">Accent color</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
              Accent Color
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="grid grid-cols-4 gap-1 p-1.5">
              {ACCENT_COLORS.map((color) => (
                <button
                  key={color.value}
                  onClick={() => setAccentColor(color.value)}
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded-md transition-all hover:scale-110",
                    color.swatch,
                    accentColor === color.value && "ring-2 ring-offset-2 ring-offset-background ring-foreground"
                  )}
                  title={color.label}
                  aria-label={`${color.label} accent color${accentColor === color.value ? " (selected)" : ""}`}
                  aria-pressed={accentColor === color.value}
                >
                  {accentColor === color.value && (
                    <Check className="h-3.5 w-3.5 text-white" aria-hidden="true" />
                  )}
                </button>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Theme toggle */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <Sun className="h-4 w-4 rotate-0 scale-100 transition-transform dark:-rotate-90 dark:scale-0" />
              <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-transform dark:rotate-0 dark:scale-100" />
              <span className="sr-only">Toggle theme</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => setTheme("light")}>
              <Sun className="mr-2 h-4 w-4" />
              Light
              {theme === "light" && <Check className="ml-auto h-3.5 w-3.5" />}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("dark")}>
              <Moon className="mr-2 h-4 w-4" />
              Dark
              {theme === "dark" && <Check className="ml-auto h-3.5 w-3.5" />}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setTheme("system")}>
              <Monitor className="mr-2 h-4 w-4" />
              System
              {theme === "system" && <Check className="ml-auto h-3.5 w-3.5" />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Idle status badge */}
        <IdleStatus />

        {/* Demo mode toggle */}
        <div className="hidden sm:flex items-center gap-1.5 rounded-md border border-dashed border-amber-400/50 bg-amber-500/10 px-2 py-1">
          <FlaskConical className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" aria-hidden="true" />
          <span className="text-[11px] font-medium text-amber-700 dark:text-amber-400 select-none">
            Demo
          </span>
          <Switch
            checked={demoMode}
            onCheckedChange={toggleDemoMode}
            className="h-4 w-7 data-[state=checked]:bg-amber-500"
            aria-label="Toggle demo mode"
          />
        </div>

        {/* Notifications bell dropdown */}
        <NotificationDropdown />
      </div>
      </header>
    </div>
  );
}
